import { useTranslation } from '@stateManagement/TranslationContext';
import { useAtom } from 'jotai';
import React, { FormEvent, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    SiteWideDemoFormField,
    SiteWideDemoFormFieldValues,
    getAllPreSubmitTransforms,
    getAllRequiredFields,
    getCurrentDisabledButtons,
    initialEmptyObject,
    getSiteWideDemoFormFields,
} from './siteWideDemoFormHelpers';
import { getCurrentProgress, preSubmitTransform } from '@helpers/formHelpers/formData';
import { isMobileAtom, selectedLanguageAtom } from '@atoms/appSettings';
import { NotificationContext } from '@contexts/NotificationContext';
import { redirectToThankyou } from '@helpers/form';
import { LazyLoadedSelectOptionsTypes, useLazyLoadedSelectOptions } from '@hooks/useLazyLoadedSelectOptions';
import { DropdownType } from '@models/customForm';
import { getTranslation } from '@helpers/languageTranslations';
import { useStaticValues } from '@stateManagement/StaticValuesContext';
import { useProvinceStateDropdown } from '@hooks/useProvinceStateDropdown';
import { useTitleDropdown } from '@hooks/useTitleDropdown';
import { useIndustryDropdown } from '@hooks/useIndustryDropdown';
import { useSubindustryDropdown } from '@hooks/useSubindustryDropdown';
import { useLocation } from 'react-router-dom';
import { postForm } from '@services/formSubmissionService';
import { FormService } from '@models/form';
import {
    Heading,
    Select,
    Text,
    Input,
    ArrowRightOutlineIcon,
    ArrowLeftOutlineIcon,
    Checkbox,
    RadioGroup,
    Button,
    colors,
    IconButton,
    CloseOutlineIcon,
    useRecaptcha,
} from '@web-for-marketing/react-ui';
import { CircularProgress, FormHelperText, Stack, Grid } from '@mui/material';
import { countryValues, getResetFieldValues, industryValues, titleValues } from '@helpers/formHelpers/staticForms';
import { siteWideDemoFormStyles as classes } from '@components/styleObjects/siteWideDemoForm';
import { logError } from '@helpers/errors';
import { fleetSizeOptions } from '@helpers/selectOptions/fleetSizeOptions';
import { StatusDots } from './StatusDots';
import {
    clearSiteWideDemoFormData,
    getSiteWideDemoFormData,
    setSiteWideDemoFormData,
} from '@services/siteWideDemoFormSessionService';
import { CustomSectionComponentProps } from '@models/customSection';
import { FormParamKeys } from '@models/form';
import { findSettingValue, sendToMarketo } from '@helpers/formHelper';
import { useLazyLoadedSiteWideDemoFormStructure } from '@hooks/useLazyLoadedSiteWideDemoFormStructure';
import { CapterraLogo } from '@icon/CapterraLogo';
import { GeotabWithR } from '@icon/GeotabWithR';
import { RatingSelect } from '@components/BlogSection/BlogComponents/Shared/RatingSelect';
import { standardFormFieldNames, standardFormSettingNames } from '@helpers/form';

const selectOptions: LazyLoadedSelectOptionsTypes[] = [DropdownType.Country, DropdownType.HearAboutUs];

export default function SiteWideDemoForm({
    params,
    displayMode,
    onClose,
}: CustomSectionComponentProps): React.ReactElement {
    const notificationContext = useContext(NotificationContext);
    const [formValues, setFormValues] = useState<SiteWideDemoFormFieldValues>(initialEmptyObject);
    const location = useLocation();
    const [page, setPage] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(true);
    const [progress, setProgress] = useState<number>(0);
    const [selectedLanguage] = useAtom(selectedLanguageAtom);
    const { selectedLanguageTranslation } = useTranslation();
    const siteWideDemoFormStructure = useLazyLoadedSiteWideDemoFormStructure();
    const [disabledButtons, setDisabledButtons] = useState<boolean[]>([true, true]);
    const updateButtons = (currentValues: SiteWideDemoFormFieldValues): void =>
        setDisabledButtons(getCurrentDisabledButtons(currentValues, siteWideDemoFormStructure.tabs));
    const siteWideDemoFormFields = getSiteWideDemoFormFields(siteWideDemoFormStructure);
    const updateProgress = (currentValues: SiteWideDemoFormFieldValues): void =>
        setProgress(
            getCurrentProgress(currentValues, getAllRequiredFields(siteWideDemoFormFields), siteWideDemoFormFields)
        );
    const htmlFormID = findSettingValue(params, 'html-form-id') || 'generic-lead-form';
    const returnURL = findSettingValue(params, 'return-url');
    const formSubtitle = findSettingValue(params, 'form-subtitle') || '';
    const formHeader = findSettingValue(params, standardFormSettingNames.formHeader) || '';
    const formRating = Number(findSettingValue(params, 'rating')) || 4.5;
    const marketingCloudID = findSettingValue(params, 'marketing-cloud-id') || 'generic-lead-form';
    const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
    const [countryOptions, hearAboutUsOptions] = useLazyLoadedSelectOptions(selectOptions);
    const [isMobile] = useAtom(isMobileAtom);
    const formRef = useRef<HTMLFormElement>(null);

    let utmSource = '';
    let utmTerm = '';
    let utmCampaign = '';
    let utmContent = '';
    let utmMedium = '';
    let gclID = '';

    getQuery();

    const siteWideFormTranslation = useMemo(
        () => getTranslation(selectedLanguageTranslation, 'siteWideDemoForm'),
        [selectedLanguageTranslation]
    );
    const paginationTranslation = useMemo(
        () => getTranslation(selectedLanguageTranslation, 'pagination'),
        [selectedLanguageTranslation]
    );
    const placeholderTranslation = useMemo(
        () => getTranslation(selectedLanguageTranslation, 'formPlaceholders'),
        [selectedLanguageTranslation]
    );
    const pageLanguageCode = useMemo(() => {
        return getTranslation(selectedLanguageTranslation, 'salesforce');
    }, [selectedLanguageTranslation]);
    const { recaptchaSiteKey } = useStaticValues();
    const { enableRecaptcha, getToken } = useRecaptcha(recaptchaSiteKey);
    const [, stateLabel, statePlaceholder, stateOptions] = useProvinceStateDropdown(formValues.country);
    const [showUserTitle, , userTitleProps, titleOptions] = useTitleDropdown(formValues.Title__c);
    const [, industryOptions] = useIndustryDropdown();
    const [subIndustryLabel, subIndustryPlaceholder, subindustryOptions] = useSubindustryDropdown(formValues.industry);

    const getSteps = (): { status: 'completed' | 'active' | 'pending' }[] => {
        return siteWideDemoFormStructure.tabs.map((_, index) => ({
            status: index + 1 === page ? 'active' : index < page ? 'completed' : 'pending',
        }));
    };

    function getQuery(): void {
        if (typeof window !== 'undefined') {
            const query = new URLSearchParams(location.search);
            utmSource = query.get('utm_source') || '';
            utmCampaign = query.get('utm_campaign') || '';
            utmContent = query.get('utm_content') || '';
            utmMedium = query.get('utm_medium') || '';
            utmTerm = query.get('utm_term') || '';
            gclID = query.get('gclid') || '';
        }
    }

    useEffect(() => {
        try {
            const demoFormData = getSiteWideDemoFormData();
            if (demoFormData) {
                setFormValues(demoFormData);
                updateButtons(demoFormData);
                setPage(demoFormData.page);
                updateProgress(demoFormData);
            }
            updateFieldValue('Lead_Source_URL__c', window.location.href);
            updateFieldValue('utm_source__c', utmSource);
            updateFieldValue('utm_campaign__c', utmCampaign);
            updateFieldValue('utm_content__c', utmContent);
            updateFieldValue('utm_medium__c', utmMedium);
            updateFieldValue('utm_term__c', utmTerm);
            updateFieldValue('GCLID__c', gclID);
            updateFieldValue(
                standardFormFieldNames.campaignId,
                findSettingValue(params, standardFormSettingNames.campaignId) ||
                    pageLanguageCode.demoFormDefaultCampaignId
            );
            updateFieldValue(
                standardFormFieldNames.leadSourceAdditionalDetails,
                findSettingValue(params, standardFormSettingNames.leadSourceAdditionalDetails) || 'Demo Requested'
            );
            updateFieldValue(
                standardFormFieldNames.leadSource,
                findSettingValue(params, standardFormSettingNames.leadSource) || 'Website - Request a Demo Form'
            );
            updateFieldValue(
                standardFormFieldNames.assetName,
                findSettingValue(params, standardFormSettingNames.assetName) || ''
            );
            updateFieldValue(
                standardFormFieldNames.assetType,
                findSettingValue(params, standardFormSettingNames.assetType) || ''
            );
            updateFieldValue(
                standardFormFieldNames.assetUrl,
                findSettingValue(params, standardFormSettingNames.assetUrl) || ''
            );
            updateFieldValue(standardFormFieldNames.formLanguageCountry, pageLanguageCode.formLanguage);
        } catch (error) {
            console.error('An error occurred retrieving form data', error);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function handleSubmit(event: FormEvent): Promise<void> {
        event.preventDefault();
        setDisabledButtons((previousDisabledButtons) =>
            previousDisabledButtons.map((value, index) => (index === previousDisabledButtons.length - 1 ? true : value))
        );
        const submitValues = preSubmitTransform(formValues, getAllPreSubmitTransforms(siteWideDemoFormFields));

        try {
            setDisableSubmit(true);
            const token = await getToken();
            if (!token) {
                throw new Error('Recaptcha validation failed. Please try again.');
            }

            const formService = sendToMarketo(formValues) ? FormService.Marketo : FormService.MarketingCloud;

            await postForm(formService, marketingCloudID, submitValues, token);
            clearSiteWideDemoFormData();
            const onFormSubmitSuccessFunction = findSettingValue<() => Promise<void>>(
                params,
            FormParamKeys.OnFormSubmitSuccessFunction
            );
            if (onFormSubmitSuccessFunction) {
                await onFormSubmitSuccessFunction();
            }
            redirectToThankyou(returnURL, selectedLanguage, [
                {
                    event: 'custom_form_submit',
                    ['form_name']: 'demo_form',
                },
            ]);
        } catch (error) {
            setDisableSubmit(false);
            const errorMessage = error instanceof Error ? error.message : error;
            notificationContext.show(errorMessage, 'error');
            updateButtons(formValues);
        }
    }

    function handleOnChange(fieldKey: string, value: string | boolean | string[] | number): void {
        const updatedValues = getResetFieldValues(fieldKey, value, [countryValues, titleValues, industryValues]);
        const updatedFormValues = { ...formValues, ...updatedValues };

        updateProgress(updatedFormValues);
        updateButtons(updatedFormValues);

        setFormValues(updatedFormValues);
    }

    function handleToggleOnChange(fieldKey: keyof SiteWideDemoFormFieldValues): void {
        handleOnChange(fieldKey, !formValues[fieldKey]);
    }

    const handleMultiSelectChange = (
        fieldKey: keyof SiteWideDemoFormFieldValues,
        value: string | string[] | number | null
    ): void => {
        const newValue = Array.isArray(value) ? value : value !== null && value !== undefined ? [value as string] : [];
        handleOnChange(fieldKey, newValue);
    };

    function handlePageChange(groupIndex: number, amount: number): void {
        const currentPage = groupIndex + 1 + amount;
        const updatedFormValues = { ...formValues, page: currentPage } as SiteWideDemoFormFieldValues;

        setPage(currentPage);
        setFormValues(updatedFormValues);
        setSiteWideDemoFormData(updatedFormValues);
    }

    function updateFieldValue(field: string, value: string | boolean | string[] | number | null): void {
        const updatedValues = getResetFieldValues(field, value, [countryValues, titleValues]);

        setFormValues((currentFormValues) => ({ ...currentFormValues, ...updatedValues }));
    }

    function updateRequiredField(field: SiteWideDemoFormField, requiredValue: boolean): void {
        if (field.required !== requiredValue) {
            field.required = requiredValue;
            updateButtons(formValues);
            updateProgress(formValues);
        }
    }

    function getFormItem(field: SiteWideDemoFormField, index: number): React.ReactElement | null {
        const showStateOptions = field.id === 'state' && stateOptions.length;
        const showSubindustryOptions = field.id === 'subindustry' && subindustryOptions.length;
        const label = field.labelKey ? siteWideFormTranslation.formField[field.labelKey] : '';
        const placeholderKey = field.placeholderKey ? placeholderTranslation[field.placeholderKey] : '';

        switch (field.type) {
            case 'text':
            case 'email':
            case 'tel':
                return (
                    <Input
                        id={field.id}
                        name={field.name}
                        value={formValues[field.name] || field.initialValue}
                        required={field.required}
                        label={label}
                        css={field.hidden ? classes.hidden : classes.selectInput}
                        placeholder={placeholderKey}
                        type={field.type}
                        shrinkLabel
                        onChange={(event) => handleOnChange(field.name, event.target.value)}
                        onFocus={enableRecaptcha}
                        disabled={field.hidden}
                    />
                );
            case 'select':
                return (
                    <Select
                        key={`${index}${field.id}${field.name}`}
                        id={field.id}
                        label={label}
                        name={field.name}
                        placeholder={placeholderKey}
                        required={field.required}
                        native={isMobile}
                        weight='regular'
                        shrinkLabel={true}
                        css={classes.selectInput}
                        options={
                            (field.id === 'industry' && industryOptions) ||
                            (field.id === 'fleet-size' && fleetSizeOptions) ||
                            (field.id === 'country' && countryOptions) ||
                            (field.id === 'title' && titleOptions) ||
                            (field.id === 'self-attribution' && hearAboutUsOptions) ||
                            []
                        }
                        value={
                            (field.id === 'industry' && industryOptions.length > 0) ||
                            (field.id === 'fleet-size' && fleetSizeOptions.length > 0) ||
                            (field.id === 'country' && countryOptions.length > 0) ||
                            (field.id === 'title' && titleOptions.length > 0) ||
                            (field.id === 'self-attribution' && hearAboutUsOptions.length > 0)
                                ? formValues[field.name]
                                : ''
                        }
                        onChange={(value) => handleOnChange(field.name, value)}
                        onFocus={enableRecaptcha}
                    />
                );
            case 'conditional-select': {
                if (showStateOptions) {
                    updateRequiredField(field, true);
                    return (
                        <Select
                            label={stateLabel}
                            placeholder={statePlaceholder}
                            options={stateOptions}
                            key={`${index}${field.id}${field.name}`}
                            id={field.id}
                            name={field.name}
                            required={field.required}
                            native={isMobile}
                            weight='regular'
                            css={classes.selectInput}
                            value={formValues[field.name]}
                            onChange={(value) => handleOnChange(field.name, value)}
                            onFocus={enableRecaptcha}
                            shrinkLabel
                        />
                    );
                }

                if (showSubindustryOptions) {
                    updateRequiredField(field, true);
                    return (
                        <Select
                            key={`${index}${field.id}${field.name}`}
                            id={field.id}
                            name={field.name}
                            required={field.required}
                            native={isMobile}
                            css={classes.selectInput}
                            onChange={(value) => handleOnChange(field.name, value)}
                            onFocus={enableRecaptcha}
                            weight='regular'
                            label={subIndustryLabel}
                            placeholder={subIndustryPlaceholder}
                            options={subindustryOptions}
                            value={formValues[field.name]}
                            shrinkLabel
                        />
                    );
                }

                updateRequiredField(field, false);
                return null;
            }
            case 'conditional-input':
                if (showUserTitle) {
                    updateRequiredField(field, true);
                    return (
                        <Input
                            key={`${index}${field.id}${field.name}`}
                            {...userTitleProps}
                            required={field.required}
                            css={classes.selectInput}
                            value={formValues[field.name] || ''}
                            onChange={(event) => handleOnChange(field.name, event.target.value)}
                            shrinkLabel
                            onFocus={enableRecaptcha}
                        />
                    );
                } else {
                    updateRequiredField(field, false);
                    return null;
                }
            case 'checkbox':
                return (
                    <Checkbox
                        label={
                            <Text
                                variant='body2'
                                dangerouslySetInnerHTML={{
                                    __html: `${label}${field.required ? ' *' : ''}`,
                                }}
                            />
                        }
                        key={`${index}${field.id}${field.name}`}
                        name={field.name}
                        checked={formValues[field.name]}
                        onChange={() => handleToggleOnChange(field.name)}
                        onFocus={enableRecaptcha}
                    />
                );
            case 'radio':
                return (
                    <RadioGroup
                        row
                        key={`${index}${field.id}${field.name}`}
                        id={field.id}
                        name={field.name}
                        value={formValues[field.name] ? '1' : '0'}
                        onFocus={enableRecaptcha}
                        onChange={(event) => handleOnChange(field.name, event.target.value === '1')}
                        options={
                            field.radioOptions
                                ? field.radioOptions.map((option) => ({
                                    content: option.content,
                                    value: option.value ? '1' : '0',
                                }))
                                : []
                        }
                        label={
                            <Text
                                variant='body2'
                                dangerouslySetInnerHTML={{
                                    __html: `${label}${field.required ? ' *' : ''}`,
                                }}
                            />
                        }
                    />
                );
            case 'multi-select-checkbox': {
                return (
                    <Grid container key={`${index}${field.id}${field.name}`}>
                        <Select
                            key={`${index}${field.id}${field.name}`}
                            id={field.id}
                            label={label}
                            name={field.name}
                            placeholder={placeholderKey}
                            required={field.required}
                            css={classes.selectInput}
                            multiple
                            value={formValues[field.name] || []}
                            onFocus={enableRecaptcha}
                            weight='regular'
                            shrinkLabel={true}
                            options={
                                Array.isArray(field.optionsArray)
                                    ? field.optionsArray.map((option: string) => ({
                                        value: option,
                                        content: option,
                                    }))
                                    : []
                            }
                            onChange={(value) => handleMultiSelectChange(field.name, value)}
                            data-testid={field.name}
                        />
                        {field.forceMultiPick && formValues[field.name].length == 1 && field.helperTextKey ? (
                            <FormHelperText css={classes.helperText(colors.core.ember)}>
                                {siteWideFormTranslation.helperText[field.helperTextKey]}
                            </FormHelperText>
                        ) : null}
                    </Grid>
                );
            }
            default:
                return null;
        }
    }

    return (
        <div css={classes.container}>
            {displayMode === 'bg-popout' ? (
                <div css={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton label='Exit form' size='micro' variant='ghost' shape='round' onClick={onClose}>
                        <CloseOutlineIcon color={colors.text.tertiary} />
                    </IconButton>
                </div>
            ) : null}
            {loading ? <CircularProgress css={classes.loadingSpinner} /> : null}
            <form
                ref={formRef}
                css={[classes.form, !loading ? classes.visible : classes.invisible]}
                data-testid='SiteWideDemoFormTestID'
                method='POST'
                id={htmlFormID}
                onSubmit={(event) => {
                    handleSubmit(event).catch((error) => logError(error, 'submitting site wide demo form'));
                }}
            >
                {formValues
                    ? siteWideDemoFormStructure.tabs.map((formGroup, groupIndex) => {
                        const isCurrentGroup = groupIndex + 1 === page;
                        return (
                            <div
                                key={`${groupIndex}${formGroup.sectionDescription}`}
                                css={[
                                    classes.formGroupContainers,
                                    page > groupIndex + 1
                                        ? classes.previousContainer(!loading)
                                        : isCurrentGroup
                                            ? classes.currentContainer
                                            : classes.nextContainer(!loading),
                                    !loading && isCurrentGroup ? classes.visible : classes.invisible,
                                ]}
                            >
                                {isCurrentGroup ? (
                                    <Stack spacing={2}>
                                        <div>
                                            <div css={classes.geotabLogo}>
                                                <GeotabWithR width={'120'} height={'30'} />
                                            </div>
                                            <StatusDots steps={getSteps()} />
                                            <div css={{ marginTop: '1.4rem' }}>
                                                <Heading
                                                    variant='h2'
                                                    color='primary'
                                                    css={classes.subtitles}
                                                    align='center'
                                                >
                                                    {formSubtitle
                                                        ? formSubtitle
                                                        : formHeader
                                                            ? formHeader
                                                            : formGroup.sectionDescription}
                                                </Heading>
                                                <Stack
                                                    direction='row'
                                                    alignItems='center'
                                                    justifyContent='center'
                                                    spacing={1}
                                                    css={classes.logoContainer}
                                                >
                                                    <CapterraLogo width={115} data-testid='capterra-logo' />
                                                    <div css={{ lineHeight: '0px' }}>
                                                        <RatingSelect
                                                            rating={formRating}
                                                            starColor={colors.action.mustard}
                                                            starSize={16}
                                                        />
                                                    </div>
                                                    <Text variant='body1' weight='bold' color='primary'>
                                                        {formRating}
                                                    </Text>
                                                </Stack>
                                            </div>
                                            <Text
                                                variant='body1'
                                                component='h3'
                                                color='primary'
                                                css={classes.hiddenAllyText}
                                                align='center'
                                            >
                                                {siteWideFormTranslation.a11yInstructions}
                                            </Text>
                                        </div>
                                        <Grid container alignItems='end'>
                                            {formGroup.formFields.length
                                                ? formGroup.formFields.map((field, index) => {
                                                    const formItem = getFormItem(field, index);
                                                    return formItem ? (
                                                        <Grid
                                                            key={`${index}${field.id}${field.name}`}
                                                            size={{ xs: 12, lg: field.width || 12 }}
                                                            css={classes.formItem}
                                                        >
                                                            {field?.startAdornmentText?.length
                                                                ? field?.startAdornmentText?.map(
                                                                    (startAdornment) => (
                                                                        <Text
                                                                            key={startAdornment.contentKey}
                                                                            {...startAdornment.props}
                                                                            style={{
                                                                                fontSize:
                                                                                          startAdornment.fontSize ||
                                                                                          '1.6rem',
                                                                            }}
                                                                        >
                                                                            {
                                                                                siteWideFormTranslation[
                                                                                    startAdornment.contentKey
                                                                                ]
                                                                            }
                                                                        </Text>
                                                                    )
                                                                )
                                                                : null}
                                                            {formItem}
                                                        </Grid>
                                                    ) : null;
                                                })
                                                : null}
                                        </Grid>
                                        {formGroup.button ? (
                                            <Grid
                                                container
                                                alignItems='center'
                                                justifyContent='center'
                                                css={classes.buttonContainer}
                                            >
                                                {groupIndex + 1 > 1 ? (
                                                    <Grid size={{ xs: 12, md: 6 }} css={classes.formItem}>
                                                        <Button
                                                            variant='secondary'
                                                            onClick={() => handlePageChange(groupIndex, -1)}
                                                            size='large'
                                                            type='button'
                                                            css={classes.button}
                                                            leftIcon={<ArrowLeftOutlineIcon />}
                                                        >
                                                            {siteWideFormTranslation.returnToPrevious}
                                                        </Button>
                                                    </Grid>
                                                ) : null}
                                                <Grid size={{ xs: 12, md: 6 }} css={classes.formItem}>
                                                    <Button
                                                        variant='primary'
                                                        disabled={
                                                            (formGroup.button.type === 'button'
                                                                ? disabledButtons[groupIndex]
                                                                : progress !== 1) ||
                                                              disableSubmit ||
                                                              formGroup.formFields.some(
                                                                  (field) =>
                                                                      field.type === 'multi-select-checkbox' &&
                                                                      field.forceMultiPick &&
                                                                      formValues[field.name]?.length === 1
                                                              )
                                                        }
                                                        aria-disabled={
                                                            formGroup.button.type === 'button'
                                                                ? disabledButtons[groupIndex]
                                                                : progress !== 1
                                                        }
                                                        size='large'
                                                        color='blue'
                                                        css={classes.button}
                                                        onClick={() => {
                                                            if (
                                                                formGroup.button.type === 'button' &&
                                                                  !disabledButtons[groupIndex]
                                                            ) {
                                                                if (formRef.current) {
                                                                    for (const formField of formGroup.formFields) {
                                                                        const inputElement =
                                                                              formRef.current.querySelector(
                                                                                  `#${formField.id}`
                                                                              );

                                                                        if (
                                                                            typeof (inputElement as HTMLInputElement)
                                                                                ?.reportValidity === 'function' &&
                                                                              !(
                                                                                  inputElement as HTMLInputElement
                                                                              ).reportValidity()
                                                                        ) {
                                                                            return;
                                                                        }
                                                                    }
                                                                }

                                                                handlePageChange(groupIndex, +1);
                                                            }
                                                        }}
                                                        data-testid={`${formGroup.button.type}-${groupIndex}`}
                                                        type={formGroup.button.type}
                                                        rightIcon={<ArrowRightOutlineIcon />}
                                                    >
                                                        {formGroup.button.text}
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        ) : null}
                                        <Text variant='body1' color='primary' css={classes.hiddenAllyText}>
                                            {`${paginationTranslation.page} ${groupIndex + 1} ${paginationTranslation.of} ${
                                                  siteWideDemoFormStructure.tabs.length
                                              }`}
                                        </Text>
                                    </Stack>
                                ) : null}
                            </div>
                        );
                    })
                    : null}
            </form>
        </div>
    );
}
